/**
 * Counting in-text citations in pasted prose: APA / Harvard author-date, MLA author-page and
 * numbered brackets.
 *
 * Author names are not ASCII, and the patterns must not be built as if they were. The FIRST
 * character of a name is `[\p{Lu}\p{Lt}\p{Lo}]` ("a surname is capitalised", with `\p{Lo}`
 * for scripts that have no case at all), the REST is `[\p{L}\p{M}'’-]` (`\p{M}` because
 * Devanagari writes vowels as combining marks). `and` and `et al.` stay Latin: they are
 * conventions of the styles detected, not of the reader's language.
 */

const NAME = "[\\p{Lu}\\p{Lt}\\p{Lo}][\\p{L}\\p{M}'’-]+";
const AUTHORS = `${NAME}(?:\\s(?:&|and)\\s${NAME})?(?:\\set\\sal\\.)?`;
// "see", "see also", "e.g.," and "cf." may open an APA parenthetical: (see Smith, 2019).
const LEAD = "(?:(?:see(?:\\salso)?|e\\.g\\.,?|cf\\.)\\s+)?";
const PAGES = "\\d{1,4}(?:\\s*[–-]\\s*\\d{1,4})?";
const LOCATOR = `(?:pp?\\.|paras?\\.|chaps?\\.)\\s*${PAGES}`;

// (Smith, 2019) (Smith & Lee, 2019a) (Smith et al., 2019, p. 4) — one item of a parenthetical.
const RE_APA_ITEM = new RegExp(
  `^${LEAD}(${AUTHORS}),\\s*\\d{4}[a-z]?(?:,\\s*${LOCATOR})?$`,
  "u"
);
// (Smith 42) (Smith and Jones 42-45) (Smith, p. 4) — one item of a parenthetical.
const RE_MLA_ITEM = new RegExp(
  `^(${AUTHORS})(?:\\s+(?:pp?\\.\\s*)?|,\\s*(?=pp?\\.)(?:pp?\\.\\s*))${PAGES}$`,
  "u"
);
const RE_PARENTHETICAL = /\(([^()]+)\)/g;
const RE_ITEM_SPLIT = /\s*;\s*/;
const RE_FIRST_WORD = /^[^\s,]+/;

// [1], [1,2], [1-3], [1, 2, 3]
const RE_NUMBERED = /\[(\d+(?:\s*[,–-]\s*\d+)*)\]/g;
// Superscript-style numbers (often plain digits after punctuation in pasted text)
const RE_SUPERSCRIPT = /(?:^|[.!?,;:])(\d{1,3})(?=\s|$|[.!?,;:])/gm;
const RE_WORDS = /\s+/;

/**
 * Cross-reference labels that take a number but are not authors: "(Table 3)", "(Figure 2)",
 * "(Study 1)". Without this every one of them reads as an MLA "(Author Page)" citation.
 */
const NON_AUTHOR_LABELS = new Set(
  [
    "appendix",
    "act",
    "box",
    "case",
    "chapter",
    "chart",
    "column",
    "condition",
    "day",
    "equation",
    "eq",
    "example",
    "exercise",
    "exhibit",
    "experiment",
    "fig",
    "figure",
    "figures",
    "group",
    "hypothesis",
    "item",
    "level",
    "line",
    "lines",
    "map",
    "model",
    "note",
    "page",
    "pages",
    "panel",
    "paragraph",
    "part",
    "phase",
    "plate",
    "question",
    "round",
    "row",
    "scene",
    "section",
    "session",
    "slide",
    "stage",
    "step",
    "study",
    "table",
    "tables",
    "task",
    "test",
    "trial",
    "version",
    "volume",
    "week",
  ].map((w) => w.toLowerCase())
);

export interface CitationResult {
  text: string;
  type: string;
}

export interface CitationStats {
  total: number;
  unique: number;
  density: string;
  byType: { type: string; count: number }[];
  citations: CitationResult[];
}

const TYPE_APA = "APA (Author, Year)";
const TYPE_MLA = "MLA (Author Page)";
const TYPE_NUMBERED = "Numbered [n]";
const TYPE_SUPERSCRIPT = "Superscript";

function isCrossReference(item: string): boolean {
  const first = RE_FIRST_WORD.exec(item)?.[0] ?? "";
  return NON_AUTHOR_LABELS.has(first.toLowerCase());
}

/** Author-date and author-page citations, including `;`-grouped and located forms. */
function collectParentheticals(text: string): {
  apa: CitationResult[];
  mla: CitationResult[];
} {
  const apa: CitationResult[] = [];
  const mla: CitationResult[] = [];
  for (const group of text.matchAll(RE_PARENTHETICAL)) {
    for (const item of group[1].trim().split(RE_ITEM_SPLIT)) {
      if (RE_APA_ITEM.test(item)) {
        apa.push({ text: `(${item})`, type: TYPE_APA });
      } else if (RE_MLA_ITEM.test(item) && !isCrossReference(item)) {
        mla.push({ text: `(${item})`, type: TYPE_MLA });
      }
    }
  }
  return { apa, mla };
}

function collect(text: string, re: RegExp, type: string, group = 0) {
  const found: CitationResult[] = [];
  for (const m of text.matchAll(re)) {
    found.push({ text: m[group], type });
  }
  return found;
}

export function countCitations(text: string): CitationStats {
  if (text.trim().length === 0) {
    return { total: 0, unique: 0, density: "0.00", byType: [], citations: [] };
  }

  const { apa, mla } = collectParentheticals(text);
  const citations: CitationResult[] = [
    ...apa,
    ...mla,
    ...collect(text, RE_NUMBERED, TYPE_NUMBERED),
  ];
  // Superscript-style numbers only count when nothing else was found.
  if (citations.length === 0) {
    citations.push(...collect(text, RE_SUPERSCRIPT, TYPE_SUPERSCRIPT, 1));
  }

  const uniqueSet = new Set(citations.map((c) => c.text));
  const wordCount = text.trim().split(RE_WORDS).filter(Boolean).length;
  const density =
    wordCount > 0 ? ((citations.length / wordCount) * 100).toFixed(2) : "0.00";

  const typeCounts = new Map<string, number>();
  for (const c of citations) {
    typeCounts.set(c.type, (typeCounts.get(c.type) ?? 0) + 1);
  }
  const byType = [...typeCounts].map(([type, count]) => ({ type, count }));

  return {
    total: citations.length,
    unique: uniqueSet.size,
    density,
    byType,
    citations,
  };
}
